import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { api } from '../lib/api';
import { useAuth } from './AuthContext';

// Types
export interface TenantBranding {
  id: string;
  tenant_id: string;
  logo_url?: string;
  logo_alt_text?: string;
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  background_color?: string;
  text_color?: string;
  font_family?: string;
  custom_css?: string;
  favicon_url?: string;
  og_image_url?: string;
  og_title?: string;
  og_description?: string;
  custom_domain?: string;
  domain_verified: boolean;
  sender_domain?: string;
  sender_domain_verified: boolean;
  spf_verified: boolean;
  dkim_verified: boolean;
  dmarc_verified: boolean;
  cname_verified: boolean;
  created_at: string;
  updated_at: string;
}

export interface BrandingContextType {
  branding: TenantBranding | null;
  isLoading: boolean;
  updateBranding: (data: Partial<TenantBranding>) => Promise<void>;
  applyBranding: () => void;
}

// Create context
const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

// Branding provider component
interface BrandingProviderProps {
  children: ReactNode;
}

export const BrandingProvider: React.FC<BrandingProviderProps> = ({ children }) => {
  const { isAuthenticated, tenant } = useAuth();
  const [branding, setBranding] = useState<TenantBranding | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load branding when authenticated
  useEffect(() => {
    if (isAuthenticated && tenant) {
      loadBranding();
    } else {
      setIsLoading(false);
    }
  }, [isAuthenticated, tenant]);

  // Apply branding when it changes
  useEffect(() => {
    if (branding) {
      applyBranding();
    }
  }, [branding]);

  const loadBranding = async () => {
    try {
      setIsLoading(true);
      const response = await api.branding.get();
      setBranding(response.data);
    } catch (error) {
      console.error('Failed to load branding:', error);
      // Continue with default branding
    } finally {
      setIsLoading(false);
    }
  };

  const updateBranding = async (data: Partial<TenantBranding>) => {
    try {
      const response = await api.branding.update(data);
      setBranding(response.data);
    } catch (error) {
      console.error('Failed to update branding:', error);
      throw error;
    }
  };

  const applyBranding = () => {
    if (!branding) return;

    const root = document.documentElement;
    
    // Apply CSS custom properties
    if (branding.primary_color) {
      root.style.setProperty('--primary-color', branding.primary_color);
    }
    if (branding.secondary_color) {
      root.style.setProperty('--secondary-color', branding.secondary_color);
    }
    if (branding.accent_color) {
      root.style.setProperty('--accent-color', branding.accent_color);
    }
    if (branding.background_color) {
      root.style.setProperty('--background-color', branding.background_color);
    }
    if (branding.text_color) {
      root.style.setProperty('--text-color', branding.text_color);
    }
    if (branding.font_family) {
      root.style.setProperty('--font-family', branding.font_family);
    }

    // Apply favicon
    if (branding.favicon_url) {
      const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (favicon) {
        favicon.href = branding.favicon_url;
      } else {
        const newFavicon = document.createElement('link');
        newFavicon.rel = 'icon';
        newFavicon.href = branding.favicon_url;
        document.head.appendChild(newFavicon);
      }
    }

    // Apply meta tags
    if (branding.og_title) {
      updateMetaTag('og:title', branding.og_title);
    }
    if (branding.og_description) {
      updateMetaTag('og:description', branding.og_description);
    }
    if (branding.og_image_url) {
      updateMetaTag('og:image', branding.og_image_url);
    }

    // Apply custom CSS
    if (branding.custom_css) {
      let customStyleElement = document.getElementById('tenant-custom-css');
      if (!customStyleElement) {
        customStyleElement = document.createElement('style');
        customStyleElement.id = 'tenant-custom-css';
        document.head.appendChild(customStyleElement);
      }
      customStyleElement.textContent = branding.custom_css;
    }
  };

  const updateMetaTag = (property: string, content: string) => {
    let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('property', property);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  };

  const value: BrandingContextType = {
    branding,
    isLoading,
    updateBranding,
    applyBranding,
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

// Hook to use branding context
export const useBranding = (): BrandingContextType => {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

// Hook to get tenant logo
export const useTenantLogo = () => {
  const { branding } = useBranding();
  
  return {
    logoUrl: branding?.logo_url,
    logoAlt: branding?.logo_alt_text || branding?.tenant_id || 'Logo',
  };
};

// Hook to get tenant colors
export const useTenantColors = () => {
  const { branding } = useBranding();
  
  return {
    primary: branding?.primary_color || '#3B82F6',
    secondary: branding?.secondary_color || '#6B7280',
    accent: branding?.accent_color || '#10B981',
    background: branding?.background_color || '#FFFFFF',
    text: branding?.text_color || '#1F2937',
  };
};
